import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";
import moment from "moment";
import { usePackingList } from "../../queries/packingListQueries";
import { useCustomers } from "../../queries/customerQueries";

const MGirthPkl = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ── Fetch packing list by ID from API ──────────────────────────────────────
  const { data: pklData, isLoading, isError, error } = usePackingList(id);

  // Fetch matching importer customer details by importer name
  const importerName = pklData?.importer || "";
  const { data: apiCustomers } = useCustomers(1, importerName);

  const importerCustomer = useMemo(() => {
    if (!importerName || !apiCustomers) return null;
    const list = apiCustomers?.data || apiCustomers?.items || (Array.isArray(apiCustomers) ? apiCustomers : []);
    if (!Array.isArray(list)) return null;
    return (
      list.find(
        (c) => c?.customerName?.trim()?.toLowerCase() === importerName.trim()?.toLowerCase()
      ) || list[0] || null
    );
  }, [apiCustomers, importerName]);

  // ── Process rounds with high-precision CBM calculations ───────────────────
  const processedRounds = useMemo(() => {
    if (!pklData?.newTeakRounds) return [];
    return pklData.newTeakRounds.map((item) => {
      const length = parseFloat(item?.length || 0);
      const girth = parseFloat(item?.girth || 0);
      const modifyGirth = parseFloat(item?.modifyGirth || 0);
      const calculatedCbm = length && girth ? (length * girth * girth) / 16000000 : 0;
      const modifyCbm = item?.modifyCbm ? parseFloat(item.modifyCbm) : 0;
      return {
        ...item,
        length,
        girth,
        modifyGirth,
        cbm: calculatedCbm.toFixed(3),
        cbmStr: calculatedCbm.toFixed(3),
        modifyCbm: modifyCbm.toFixed(3),
      };
    });
  }, [pklData]);

  // ── Chunk into pages of 50 rows ────────────────────────────────────────────
  const chunkData = (data, chunkSize) => {
    const result = [];
    if (!data) return result;
    for (let i = 0; i < data.length; i += chunkSize) {
      result.push(data.slice(i, i + chunkSize));
    }
    return result;
  };

  const chunkedData = useMemo(() => chunkData(processedRounds, 50), [processedRounds]);

  // ── Group 3 tables per PDF page ────────────────────────────────────────────
  const groupedChunks = useMemo(() => {
    const result = [];
    for (let i = 0; i < chunkedData.length; i += 3) {
      result.push(chunkedData.slice(i, i + 3));
    }
    return result;
  }, [chunkedData]);

  // ── Per-table CBM total — respects mtsNewValue flag ───────────────────────
  const calculateTotalCBM = (chunk) => {
    return chunk
      .reduce((total, item) => {
        const value = pklData?.mtsNewValue ? item?.cbm : item?.modifyCbm;
        return Number(total) + Number(value || 0);
      }, 0)
      .toFixed(3);
  };

  // ── Summary totals ─────────────────────────────────────────────────────────
  const totalNos = useMemo(() => processedRounds.length, [processedRounds]);

  // ── Only force a page break before summary when there are multiple page groups
  // (i.e. more than 150 rows = more than 1 group of 3 tables × 50 rows each)
  // For small datasets, the summary should flow naturally on the same page.
  const summaryNeedsPageBreak = groupedChunks.length > 1;

  // ── PDF download ───────────────────────────────────────────────────────────
  const downloadPDF = () => {
    const element = document.getElementById("mgirth-pdf-content");
    if (!element) {
      console.error("PDF content element not found!");
      return;
    }

    const originalWidth = element.style.width;
    element.style.width = "800px";

    const options = {
      margin: 5,
      filename: `${pklData?.mtsNewValue ? "B-" : "M-GIRTH "}PKL - ${pklData?.pkl || ""}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 3 },
      jsPDF: { unit: "mm", format: [220, 385], orientation: "portrait" },
    };

    html2pdf()
      .set(options)
      .from(element)
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        element.style.width = originalWidth;
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.setTextColor(0, 0, 0);
          pdf.text(`Page ${i}`, pdf.internal.pageSize.getWidth() / 2, 7, {
            align: "center",
          });
        }
      })
      .save();
  };

  // ── Header ─────────────────────────────────────────────────────────────────
  const Header = () => {
    const importerHeading = (importerCustomer?.customerName || pklData?.importer || "BEST TIMBERS").toUpperCase();
    const doorNo = importerCustomer?.billingDoorNo;
    const street = importerCustomer?.billingStreet;
    const landmark = importerCustomer?.billingLandmark;
    const city = importerCustomer?.billingCity;
    const district = importerCustomer?.billingDistrict;
    const pincode =
      importerCustomer?.billingPinCode ||
      importerCustomer?.billingPincode ||
      importerCustomer?.pinCode ||
      importerCustomer?.pincode;

    const fromMob = pklData?.fromNumber || pklData?.fromMobileNumber || "";
    const fromStreetStr = pklData?.fromStreet ? pklData.fromStreet.toUpperCase() : "";
    const fromLandmarkStr = pklData?.fromLandmark ? pklData.fromLandmark.toUpperCase() : "";
    const fromCityStr = pklData?.fromCity ? pklData.fromCity.toUpperCase() : "";
    const fromDistrictStr = pklData?.fromDistrict ? pklData.fromDistrict.toUpperCase() : "";
    const fromPincodeStr = pklData?.fromPincode || "";

    const rawDate = pklData?.plDate || pklData?.date || "";
    const formattedDate = rawDate && moment(rawDate).isValid()
      ? moment(rawDate).format("DD-MM-YYYY")
      : rawDate;

    const hasBillingAddress = Boolean(doorNo || street || city || pincode);

    return (
      <div className="header-section" style={{ marginBottom: "0px", position: "relative" }}>
        {/* M-/B- badge top right */}
        <span style={{ position: "absolute", top: "0px", right: "0px", fontWeight: "bold", fontFamily: "Cambria", fontSize: "14px" }}>
          {pklData?.mtsNewValue ? `B-${pklData?.pkl}` : `M-${pklData?.pkl}`}
        </span>

        <div className="row p-0 m-0">
          {/* From */}
          <div className="col-6">
            <div className="row">
              <div className="col-12 pkl-download-from-to-address-head p-0 m-0">From</div>
            </div>
            <div className="row p-0 m-0">
              <div className="col-1"></div>
              <div className="col-11">
                {/* Original hardcoded From section (preserved):
                <h6 className="pkl-download-heading">BEST TIMBERS</h6>
                <p className="pkl-download-from-address">New.NO.515, Old.No. 274,</p>
                <p className="pkl-download-from-address">Sydenhams Road,</p>
                <p className="pkl-download-from-address">Nehru Timber Market, Choolai</p>
                <p className="pkl-download-from-address">Chennai - 600112</p>
                */}
                <h6 className="pkl-download-heading">{importerHeading}</h6>
                {hasBillingAddress ? (
                  <>
                    {doorNo && <p className="pkl-download-from-address">{doorNo}</p>}
                    {street && (
                      street.includes(",") && street.length > 25 ? (
                        (() => {
                          const commaIndex = street.indexOf(",");
                          const line1 = street.slice(0, commaIndex + 1).trim();
                          const line2 = street.slice(commaIndex + 1).trim();
                          return (
                            <>
                              {line1 && <p className="pkl-download-from-address">{line1}</p>}
                              {line2 && <p className="pkl-download-from-address">{line2}</p>}
                            </>
                          );
                        })()
                      ) : (
                        <p className="pkl-download-from-address">{street}</p>
                      )
                    )}
                    {landmark && <p className="pkl-download-from-address">{landmark}</p>}
                    {(city || pincode) && (
                      <p className="pkl-download-from-address">
                        {`${city || ""}${district ? `, ${district}` : ""}${pincode ? ` - ${pincode}` : ""}`}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="pkl-download-from-address">New.NO.515, Old.No. 274,</p>
                    <p className="pkl-download-from-address">Sydenhams Road,</p>
                    <p className="pkl-download-from-address">Nehru Timber Market, Choolai</p>
                    <p className="pkl-download-from-address">Chennai - 600112</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* To */}
          <div className="col-6">
            <div className="row">
              <div className="col-8 pkl-download-from-address text-end p-0 m-0" style={{ fontStyle: "normal" }}>
                <b>DATE : {formattedDate}</b>
              </div>
            </div>
            <div className="row p-0 m-0">
              <div className="col-2 pkl-download-from-to-address-head">To</div>
              <div className="col-10 px-4">
                <p className="pkl-download-address py-1">{pklData?.fromName?.toUpperCase() || ""}</p>
                <p className="pkl-download-address py-1">{`${fromMob}${fromStreetStr}`},</p>
                <p className="pkl-download-address py-1">{`${fromLandmarkStr}, ${fromCityStr}, `}</p>
                <p className="pkl-download-address py-1">{`${fromDistrictStr}-${fromPincodeStr} `}</p>
                <p className="pkl-download-address py-1">GST NO : {pklData?.gstNumber || ""}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Item / PKL / Lorry row */}
        <div className="row p-0 m-0">
          <div className="col-4 p-0 mt-1 mb-0">
            <i style={{ fontSize: "15px" }}>Item : </i>
            <span className="pkl-download-from-address m-0" style={{ fontWeight: "bold", fontStyle: "normal", fontSize: "17px" }}>
              TEAK ROUND LOGS
            </span>
          </div>
          <div className="col-4 p-0 mt-1 mb-0 text-center">
            <h6 className="pkl-download-from-address" style={{ fontWeight: "bold", fontStyle: "normal", fontSize: "17px", margin: 0, padding: 0 }}>
              PKL-{pklData?.pkl || ""}
            </h6>
          </div>
          <div className="col-4 p-0 mt-1 mb-0">
            <i style={{ fontSize: "15px" }}>Lorry No : </i>
            <span className="pkl-download-from-address" style={{ fontWeight: "bold", fontStyle: "normal", fontSize: "17px" }}>
              {pklData?.lorry || ""}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center bg-light" style={{ height: "100vh", width: "100vw" }}>
        <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <h6 className="mt-3 text-muted fw-semibold">Fetching M-Girth packing list details...</h6>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (isError) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center bg-light p-4" style={{ height: "100vh", width: "100vw" }}>
        <div className="card shadow border-0 p-4 text-center" style={{ maxWidth: "450px" }}>
          <div className="text-danger mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" className="bi bi-exclamation-triangle" viewBox="0 0 16 16">
              <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.146.146 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.163.163 0 0 1-.054.06.116.116 0 0 1-.066.017H1.146a.115.115 0 0 1-.066-.017.163.163 0 0 1-.054-.06.176.176 0 0 1 .002-.183L7.884 2.073a.147.147 0 0 1 .054-.057zm1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z" />
              <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z" />
            </svg>
          </div>
          <h5 className="fw-bold mb-2">Failed to load M-Girth report</h5>
          <p className="text-muted small mb-4">{error?.message || "An unexpected error occurred while fetching the record details."}</p>
          <button className="btn btn-secondary w-100 fw-semibold" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Scoped CSS — same classes reused from PKLDownload, no Tailwind inside table ── */}
      <style>{`
        .header-section {
          margin-bottom: 20px;
        }
        .pkl-download-from-to-address-head {
          font-family: Cambria;
          font-size: 20px;
          font-weight: bold;
          color: #000;
        }
        .pkl-download-heading {
          font-family: Calisto MT;
          font-size: 28px;
          font-weight: bold;
          font-style: italic;
          margin: 0;
          color: #000;
        }
        .pkl-download-from-address {
          font-family: Cambria;
          font-size: 16.5px;
          font-style: italic;
          margin: 2px 0px;
          line-height: 1.2;
          font-weight: 500;
          color: #000;
          white-space: normal;
          word-wrap: break-word;
          overflow-wrap: break-word;
          max-width: 320px;
        }
        .pkl-download-address {
          font-family: Cambria;
          font-size: 13px;
          border-bottom: 1.5px solid black;
          margin: 2px 0px;
          line-height: normal;
          font-weight: 500;
          color: #000;
          width: 100%;
          display: block;
          box-sizing: border-box;
          white-space: normal;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        .pkl-download-table-head {
          border-bottom: 1px solid black;
          border-right: 1px solid black;
          padding: 1px;
          font-family: Calibri;
          font-size: 13px;
          font-weight: bold;
          text-align: center !important;
          background-color: #fff;
          color: #000;
        }
        .pkl-download-table-data {
          border-bottom: 1px solid black;
          border-right: 1px solid black;
          padding: 1px;
          font-family: Calibri;
          font-size: 12.5px;
          font-weight: 400;
          text-align: center !important;
          color: #000;
        }
      `}</style>

      {/* ── Action Buttons ── */}
      <button
        onClick={downloadPDF}
        style={{ position: "absolute", right: "10px", top: "10px", zIndex: 10 }}
        className="btn btn-success"
      >
        Download PDF
      </button>

      {/* SVG back arrow — no external asset needed */}
      <div
        style={{ width: "45px", height: "45px", cursor: "pointer", position: "absolute", top: "8px", left: "40px", zIndex: 10 }}
        onClick={() => navigate(-1)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
      </div>

      {/* ── PDF Capture Area ── */}
      <div className="container px-5" id="mgirth-pdf-content" style={{ overflow: "auto" }}>

        {groupedChunks && groupedChunks.length > 0 ? (
          groupedChunks.map((tableGroup, groupIndex) => (
            <div key={groupIndex} style={{ pageBreakBefore: groupIndex > 0 ? "always" : "auto" }}>
              <Header />

              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", marginTop: "15px" }}>
                {tableGroup.map((chunk, tableIndex) => {
                  const startIndex = (groupIndex * 3 + tableIndex) * 50 + 1;
                  const totalTableCBM = calculateTotalCBM(chunk);

                  return (
                    <div key={tableIndex} style={{ width: "32%", marginBottom: "10px", pageBreakInside: "avoid" }}>
                      {/*
                        borderCollapse:collapse + explicit border on every th/td
                        → works cleanly with Tailwind preflight disabled (tailwind.config.js)
                      */}
                      <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid black" }}>
                        <thead>
                          <tr>
                            <th className="pkl-download-table-head" style={{ border: "1px solid black", backgroundColor: "#fff" }}>S.NO</th>
                            <th className="pkl-download-table-head" style={{ border: "1px solid black", backgroundColor: "#fff" }}>LENGTH</th>
                            <th className="pkl-download-table-head" style={{ border: "1px solid black", backgroundColor: "#fff" }}>GIRTH</th>
                            <th className="pkl-download-table-head" style={{ border: "1px solid black", backgroundColor: "#fff" }}>CBM</th>
                          </tr>
                        </thead>
                        <tbody>
                          {chunk.map((item, index) => {
                            const continuousIndex = startIndex + index;
                            return (
                              <tr key={index}>
                                <td className="pkl-download-table-data" style={{ textAlign: "center", border: "1px solid black", backgroundColor: "#fff" }}>
                                  {continuousIndex}
                                </td>
                                <td className="pkl-download-table-data" style={{ textAlign: "center", border: "1px solid black", backgroundColor: "#fff" }}>
                                  {item?.length}
                                </td>
                                <td className="pkl-download-table-data" style={{ textAlign: "center", border: "1px solid black", backgroundColor: "#fff" }}>
                                  {/* mtsNewValue flag: true → original girth, false → modified girth */}
                                  {pklData?.mtsNewValue ? item?.girth : item?.modifyGirth}
                                </td>
                                <td className="pkl-download-table-data" style={{ textAlign: "center", border: "1px solid black", backgroundColor: "#fff" }}>
                                  {/* mtsNewValue flag: true → original cbm, false → modified cbm */}
                                  {pklData?.mtsNewValue ? item?.cbmStr : item?.modifyCbm}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan="3" style={{ border: "1px solid black", padding: "5px", textAlign: "right", fontWeight: "bold", fontSize: "11px", backgroundColor: "#fff", fontFamily: "Calibri" }}>
                              <b>Total</b>
                            </td>
                            <td style={{ border: "1px solid black", padding: "5px", textAlign: "center", fontSize: "11px", backgroundColor: "#fff", fontFamily: "Calibri" }}>
                              {totalTableCBM}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div>
            <Header />
            <div className="text-center py-5 text-muted border border-dashed rounded mt-4">
              No M-Girth log details available in this packing list.
            </div>
          </div>
        )}

        {/* ── Summary Section ── */}
        <div id="mgirth-summary-section" style={{ pageBreakBefore: summaryNeedsPageBreak ? "always" : "auto", marginTop: "20px" }}>
          {summaryNeedsPageBreak && <Header />}
          <table>
            <thead>
              <tr>
                <th colSpan="2" style={{ padding: "2px", fontWeight: "bold", textAlign: "left" }}>
                  SUMMARY DETAILS
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "5px", fontSize: "12px", width: "150px" }}><b>Total CBM</b></td>
                <td style={{ padding: "5px", fontSize: "12px" }}>
                  <b>{pklData?.mtsNewValue ? pklData?.invCBM : pklData?.modifyTotalCBM}</b>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "5px", fontSize: "12px" }}><b>Total Nos</b></td>
                <td style={{ padding: "5px", fontSize: "12px" }}>{totalNos}</td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </>
  );
};

export default MGirthPkl;
